/* eslint-disable no-use-before-define */
import { useContext, useCallback, useEffect, useRef, useState } from 'react';
import _ from 'underscore';
import { createErrorMessage } from '@bingads-webui-campaign/error-utilities';
import getGreenId from '@bingads-webui-clientcenter/greenid-util-smart';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { useAccountPatch, useCustomerFetch, useUserFetch, useUserPatch, useAcquisitionCouponRedeem, useAcquisitionCouponAssociate } from '../use-entity';
import { useNotifications } from '../notifications';
import { useTelemetry } from '../use-telemetry';
import useFeatures from '../use-features';
import LifecycleEvent from '../lifecycle-event';

export default function useSaveHandler({
  scope,
  preselectedPiid,
  setSaveHandler,
  onStateChange,
  hideTerms,
  // TODO: maybe come up with a better name...
  activate: activateFunc,
  coupon,
  cfar,
  currency,
}) {
  const { trace: log } = useTelemetry();
  const dataRef = useRef();
  const { paymentContext: { GreenIdUrl } } = useContext(PaymentContext);

  const updateAccount = useAccountPatch(scope);
  const associatePaymentInstrument = useCallback((piid) => {
    const { paymentType } = dataRef.current;
    const paymentOption = paymentType === Constants.PaymentTypeId.Prepay
      ? Constants.PaymentOption.Prepay
      : Constants.PaymentOption.Threshold;

    // QUESTION: what happens if we have both an acquisition coupon (auto-PBC) and a user-added code?
    return updateAccount({
      Account: {
        PaymentType: paymentType,
        PaymentOption: paymentOption,
        PreferredBillToPaymentInstrumentId: piid,
      },
    }).then(() => log(`PaymentInstrument associated. ${piid} -> ${scope.aid}`));
  }, [scope.aid, updateAccount, log]);

  const logError = useLogError();
  const { notify } = useNotifications();

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, arrow-body-style
  const refreshPaymentInstruments = useCallback(() => {
    // TODO: pick up the latest data from the server
    // NOTE: this should be relevant only if the save failed and the user needs to try again
    return Promise.resolve();
  }, []);

  const redeemAcquisitionCoupon = useAcquisitionCoupon({ scope, currency, coupon });

  const [isAcquisitionCouponNoPIEnabled] = useFeatures('AcquisitionCouponNoPI').values;
  const associateAcquisitionCouponRef = useRef(() => Promise.resolve());
  associateAcquisitionCouponRef.current = useAcquisitionCouponAssociate(scope, { currency, greenId: getGreenId(GreenIdUrl) });

  // NOTE: we don't want to recreate the handler if activate becomes a noop (which will happen as soon as the account is activated)
  const activateRef = useRef();
  activateRef.current = activateFunc;

  const handleSkipPi = useCallback(() => {
    const activate = activateRef.current;
    // (the account should move automatically from "pending" to "active" when a PI is added in post signup)
    const activateAccount = !activate
      ? Promise.resolve()
      : updateAccount({
        Account: {
          LifeCycleStatus: Constants.AccountLifecycleStatus.Pending,
        },
        ...cfar,
      }).then(() => {
        log(`Account activated: ${scope.aid} (pending PI)`);
        activate();
      });

    const isCouponEligibleForAssociation = isAcquisitionCouponNoPIEnabled
      && coupon
      && (coupon.type === 'ccl' || coupon.type === 'class')
      && coupon.isCouponValid
      && coupon.couponPurposeId === 0; // CouponPurposeId: 0 == SignUp

    log(`[CouponAssociationOnSkip] eligible=${!!isCouponEligibleForAssociation} `
      + `isAcquisitionCouponNoPIEnabled=${!!isAcquisitionCouponNoPIEnabled} `
      + `hasCoupon=${!!coupon} `
      + `type=${_.get(coupon, 'type')} `
      + `isCouponValid=${_.get(coupon, 'isCouponValid')} `
      + `couponPurposeId=${_.get(coupon, 'couponPurposeId')}`);

    const associateCouponOnSkip = isCouponEligibleForAssociation
      ? () => associateAcquisitionCouponRef.current({
        website: coupon.website,
        couponCode: coupon.code,
        codeType: coupon.type,
      }).catch(err => log(`AssociateCoupon on skip failed (non-blocking): ${err}`))
      : () => Promise.resolve();

    return activateAccount
      .then(associateCouponOnSkip)
      .then(() => onStateChange({ type: LifecycleEvent.DataSaved }))
      .catch(invoke(notify, logError('handleSkipPi failed: '), error => onStateChange({ type: LifecycleEvent.SaveFailed, args: { error } })));
  }, [cfar, coupon, isAcquisitionCouponNoPIEnabled, log, logError, notify, onStateChange, scope.aid, updateAccount]);

  const handlePiAddSuccess = useCallback((piid) => {
    log(`Using PaymentInstrument: ${piid}`);

    refreshPaymentInstruments(piid).catch(logError('handlePiAddSuccess-refreshPaymentInstruments failed: '));
    // NOTE: once the save has been triggered, cache the current state of the activate function for the duration
    const activate = activateRef.current;
    // (the account should move automatically from "pending" to "active" when a PI is added)
    const activateAccount = !activate
      ? Promise.resolve()
      : updateAccount({
        Account: {
          LifeCycleStatus: Constants.AccountLifecycleStatus.Pending,
        },
        ...cfar,
      }).then(() => {
        log(`Account activated: ${scope.aid} (pending PI)`);
        activate();
      });

    // TODO: how to add funds?
    // TODO: lock payment setting if association succeeds
    return activateAccount
      .then(() => associatePaymentInstrument(piid))
      .then(redeemAcquisitionCoupon)
      .then(() => onStateChange({ type: LifecycleEvent.DataSaved }))
      .catch(invoke(notify, logError('handlePiAddSuccess failed: '), error => onStateChange({ type: LifecycleEvent.SaveFailed, args: { error } })));
  }, [cfar, onStateChange, notify, log, logError, scope.aid, updateAccount, associatePaymentInstrument, refreshPaymentInstruments, redeemAcquisitionCoupon]);

  const {
    savePiAttempted,
    saveTermsAttempted,
    attachSubmitEvent,
    detachSubmitEvent,
  } = usePaymentInstrumentCreation({
    scope,
    setSaveHandler,
    onStateChange,
    hideTerms: hideTerms || !activateFunc,
    dataRef,
    handlePiAddSuccess,
    handleSkipPi,
  });

  const [isLoading, setIsLoading] = useState(true);
  const onDataChange = useCallback((event) => {
    if (event.type === LifecycleEvent.DataChanged) {
      dataRef.current = event.args.data;
    } else if (event.type === LifecycleEvent.DataLoaded) {
      setIsLoading(false);
    }

    onStateChange(event, event.args);
  }, [onStateChange, setIsLoading]);

  // NOTE: some scenarios, e.g. PayPal, involve reloading the page
  // with the newly created PI already selected in the context;
  // in such cases we should automatically resume the save process
  useAutoSave({
    handlePiAddSuccess,
    onStateChange,
    piid: preselectedPiid,
    dataRef,
    isLoading,
  });

  return {
    savePiAttempted,
    saveTermsAttempted,
    attachSubmitEvent,
    detachSubmitEvent,
    handlePiAddSuccess,
    onDataChange,
  };
}

// NOTE: this is a low level hook, that attempts to hide handling inconsistencies between different payment instruments;
// ideally the AddPIForm component should have done this itself, presenting a unified abstraction layer over all payment instruments,
// alas, as it's so often the case, it's just a leaky abstraction
function usePaymentInstrumentCreation({
  scope,
  setSaveHandler,
  onStateChange,
  hideTerms,
  dataRef,
  handlePiAddSuccess,
  handleSkipPi,
}) {
  const createPaymentInstrument = useRef(_.noop);
  const attachSubmitEvent = useCallback((submit) => {
    createPaymentInstrument.current = submit;
  }, []);
  const detachSubmitEvent = useCallback(() => {
    createPaymentInstrument.current = _.noop;
  }, []);

  // NOTE: we don't want to regenerate the callback on every data change, so we use a ref
  const { trace: log } = useTelemetry();
  const logError = useLogError();
  const updateUser = useUserPatch(scope);
  const [savePiAttempted, setSavePiAttempted] = useState(false);
  const [saveTermsAttempted, setSaveTermsAttempted] = useState(false);

  // For User who select Country such as DE, AllowContactByPhone will be shown in T&C
  // If user check the AllowContactByPhone, the CCMTErrorNumber_ContactPhone1Invalid error will be triggered if Phone1 in user object is empty
  // Update the phone1 in user object with customer.BusinessPhone as a workaround
  const isAllowContactByPhoneExist = dataRef.current && _.get(dataRef.current, ['terms', 'communication', 'AllowContactByPhone']) !== undefined;
  const { data: customer } = useCustomerFetch(scope, isAllowContactByPhoneExist);
  const { data: user } = useUserFetch(scope, isAllowContactByPhoneExist);

  useEffect(() => {
    setSaveHandler((isSkipPi = false) => {
      setSaveTermsAttempted(true);
      setSavePiAttempted(!isSkipPi);

      const { piid, piValidation, terms } = dataRef.current;
      // TODO: should we check for coupon validation errors too?
      if (piid) {
        // if we already have a preselected PI, just go ahead and associate it
        // (this usually occurs as part of the PayPal flow)
        handlePiAddSuccess(piid);
      } else if ((piValidation || isSkipPi) && (hideTerms || terms.accepted)) {
        // NOTE: we need to save any agreement information now,
        // because payment instrument creation can trigger a page reload and we would lose this data
        let updateUserPromise = Promise.resolve();
        if (terms.accepted) {
          // NOTE: there is no dedicated operation to save the user's consent, it's implied by the account activation
          const updateUserContent = {
            ...dataRef.current.terms.communication,
          };

          // only update user phone1 if AllowContactByPhone is checked and user phone1 is empty and customer.BusinessPhone is not empty
          if (
            isAllowContactByPhoneExist
            && dataRef.current.terms.communication.AllowContactByPhone
            && !_.get(user, ['ContactInfo', 'Phone1'])
            && _.get(customer, ['BusinessPhone'])
          ) {
            updateUserContent.ContactInfo = {
              ...user.ContactInfo,
              Phone1: customer.BusinessPhone,
            };
          }

          updateUserPromise = updateUser({
            User: {
              ...updateUserContent,
            },
          }).then(() => {
            const isOptIn = updateUserContent.AllowContactByPhone || updateUserContent.AllowContact;
            log(`[CommunicationPref][Signup] User opt in: ${isOptIn}. AllowContact: ${updateUserContent.AllowContact}. AllowContactByPhone: ${updateUserContent.AllowContactByPhone}`);
          }).catch((error) => {
            onStateChange({ type: LifecycleEvent.SaveFailed, args: { error } });
            logError('piCreate-saveCommPrefs failed: ')(error);
          });
        }

        if (isSkipPi) {
          updateUserPromise.then(() => {
            handleSkipPi();
          });
        } else {
          // this will eventually, indirectly, call handlePiAddSuccess, if everything goes well
          updateUserPromise.then(() => {
            createPaymentInstrument.current.call();
          });
        }
      } else {
        onStateChange({ type: LifecycleEvent.SaveFailed, args: { error: { Errors: [{ Code: 'ClientSide_ValidationFailure' }] } } });
      }
    });
  }, [customer, dataRef, handlePiAddSuccess, handleSkipPi, hideTerms, isAllowContactByPhoneExist, log, logError, onStateChange, setSaveHandler, updateUser, user]);

  return {
    savePiAttempted,
    saveTermsAttempted,
    attachSubmitEvent,
    detachSubmitEvent,
  };
}

function useAutoSave({
  handlePiAddSuccess,
  onStateChange,
  piid,
  dataRef,
  isLoading,
}) {
  const { trace: log } = useTelemetry();
  useEffect(() => {
    if (piid) {
      log(`Auto-saving PaymentInstrument: ${piid}`);

      onStateChange({ type: LifecycleEvent.DataLoaded });
      onStateChange({ type: LifecycleEvent.SavingData });
    }
  }, [piid, onStateChange, log]);

  useEffect(() => {
    if (piid && !isLoading) {
      if (dataRef.current.piid === piid) {
        handlePiAddSuccess(piid);
      } else {
        // NOTE: this should not really happen, because it means somehow the payment settings conflict with each other
        log(`Auto-saving PaymentInstrument failed (PIID reset): ${dataRef.current.piid}`);
        onStateChange({ type: LifecycleEvent.SaveFailed, args: { error: createErrorMessage('PaymentInstrumentReset') } });
      }
    }
  }, [piid, dataRef, handlePiAddSuccess, onStateChange, log, isLoading]);
}

function useAcquisitionCoupon({ scope, currency, coupon }) {
  const logError = useLogError();
  const { notify } = useNotifications();
  const { trace: log } = useTelemetry();
  const { paymentContext: { GreenIdUrl } } = useContext(PaymentContext);

  // NOTE: the parameters of the coupon call can fluctuate while the data is loading;
  // we don't want to keep regenerating handles, in cascade, as it can have other bad side effects
  const redeemAcquisitionCouponRef = useRef(() => Promise.resolve());
  redeemAcquisitionCouponRef.current = useAcquisitionCouponRedeem(scope, { currency, greenId: getGreenId(GreenIdUrl) });

  const redeemAcquisitionCoupon = useCallback(() => {
    if (coupon) {
      log(`Redeeming acquisition coupon (type=${coupon.type})`);
      return redeemAcquisitionCouponRef.current({
        website: coupon.website,
        couponCode: coupon.code,
        codeType: coupon.type,
      }).then((data) => {
        if (data && data.ErrorTitle) {
          const error = {
            ErrorTitle: data.ErrorTitle,
            ErrorMessage: data.ErrorMessage,
          };

          logError('AcquisitionCoupon failed: ')(error);
        }
      }).catch(invoke(notify, logError('AcquisitionCoupon failed: ')));
    }

    return Promise.resolve();
  }, [coupon, logError, log, notify]);

  return redeemAcquisitionCoupon;
}

function useLogError() {
  const { error: log } = useTelemetry();
  const logError = useCallback(msg => error => log(`${msg}${createErrorMessage(error)}`), [log]);

  return logError;
}

function invoke(...functions) {
  return (...args) => _.each(functions, fn => fn(...args));
}
